import { useChartSeries } from '../ChartSeriesContext'
import { formatChartValue } from '../chartFormatting'

import { buildRankedRows } from './buildRankedRows'

import type { JSX } from 'react'

interface BarDataTableProps {
  /** Names the table (the chart's accessible label). */
  caption: string
}

/**
 * A screen-reader data alternative for the ranked bar chart, rendered inside
 * {@link ChartFrame}'s visually-hidden disclosure. Reads the series registry from
 * context (same source, and same ranking, as the bars) so it can't drift: one row
 * per channel with its value as text — the value the SVG bars can't announce on
 * their own.
 */
export function BarDataTable({
  caption,
}: BarDataTableProps): JSX.Element | null {
  const series = useChartSeries()
  const rows = buildRankedRows(series)

  if (rows.length === 0) return null

  const seriesById = new Map(series.map((s) => [s.id, s]))
  const summary = rows
    .map((r) => {
      const s = seriesById.get(r.id)
      return `${s?.label ?? r.id}: ${formatChartValue(r.value, s?.unit)}`
    })
    .join('. ')

  return (
    <table>
      <caption>{`${caption}. ${summary}`}</caption>
      <thead>
        <tr>
          <th scope="col">Channel</th>
          <th scope="col">Value</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const s = seriesById.get(r.id)
          return (
            <tr key={r.id}>
              <th scope="row">{s?.label ?? r.id}</th>
              <td>{formatChartValue(r.value, s?.unit)}</td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
